import { SdkContext } from '../internal'

/** F-197b: the providers whose playback the community can sign with its own key. */
export type VideoKeyProvider = 'bunny' | 'cloudflare'

export type VideoKeyStatus = 'unverified' | 'valid' | 'invalid'

/** One provider's key as owner and admin see it: never the key, only its last 4 characters. */
export interface VideoIntegration {
  provider: VideoKeyProvider
  configured: boolean
  masked: string | null
  last4: string | null
  status: VideoKeyStatus | null
  checkedAt: string | null
  updatedAt: string | null
}

export interface VideoIntegrations {
  providers: VideoIntegration[]
  /** How long a signed player lasts, in seconds (6 hours). */
  signedTtlSeconds: number
}

/** Bunny takes `tokenKey`; Cloudflare takes `privateKey` (PEM, base64 or JWK) and `keyId` (optional with a JWK). */
export interface VideoKeyInput {
  tokenKey?: string
  keyId?: string
  privateKey?: string
}

/**
 * F-197b: `/api/video-integrations`, owner and admin of the `X-CommunityId`
 * (moderator, finance and member get 403). Write-only keys.
 */
export class VideoIntegrationsResource {
  constructor(private ctx: SdkContext) {}

  async list(): Promise<VideoIntegrations> {
    return this.ctx.call({ httpMethod: 'GET', path: '/video-integrations' })
  }

  /** Saves or replaces the key; the status starts over at `unverified`. */
  async save(provider: VideoKeyProvider, body: VideoKeyInput): Promise<VideoIntegration> {
    return this.ctx.call({ httpMethod: 'PUT', path: '/video-integrations/{provider}', hasBody: true }, { provider, body })
  }

  async remove(provider: VideoKeyProvider): Promise<{ provider: VideoKeyProvider; deleted: boolean }> {
    return this.ctx.call({ httpMethod: 'DELETE', path: '/video-integrations/{provider}' }, { provider })
  }

  /** Signs a link of one of the community's videos and asks the provider once (422 `videoNotFound`, 502 unreachable). */
  async verify(provider: VideoKeyProvider, videoUrl: string): Promise<VideoIntegration> {
    return this.ctx.call({ httpMethod: 'POST', path: '/video-integrations/{provider}/verify', hasBody: true }, { provider, body: { videoUrl } })
  }
}
